import type { BusRoute, MapVehicle, Stop, TransitDataset } from '@/types'

import { haversineMeters } from './stopIndex'

export const DISCOVERY_SLICE_COUNT = 6
export const VEHICLE_LOOKAHEAD_STOPS = 2

export interface VehicleStateTarget {
  stationId: number
  routeCode: string
  plate?: string
}

export function collectRouteStationIds(route: BusRoute): number[] {
  const stationIds = new Set<number>()
  for (const direction of ['outbound', 'inbound'] as const) {
    for (const stop of route.directions[direction].stops) stationIds.add(stop.id)
  }
  return [...stationIds]
}

/**
 * Round-robin slices keep each slice within ceil(N / 6), unlike contiguous
 * floor-based boundaries whose final slice can silently grow after mutations.
 */
export function discoveryStationSlice(stationIds: number[], slice: number): number[] {
  const normalized =
    ((slice % DISCOVERY_SLICE_COUNT) + DISCOVERY_SLICE_COUNT) % DISCOVERY_SLICE_COUNT
  return stationIds.filter((_stationId, index) => index % DISCOVERY_SLICE_COUNT === normalized)
}

export function stableVehicleIdentity(vehicle: MapVehicle): string {
  if (vehicle.plate) {
    return `${vehicle.routeCode}|${vehicle.fleetCode ?? ''}|plate:${vehicle.plate}`
  }
  // The protocol supplies no exact filter for an unplated row. Preserve the
  // parser's established fallback ID rather than inventing a synthetic plate.
  return `${vehicle.routeCode}|fallback:${vehicle.id}`
}

export function aheadVehicleCheckpoint(
  route: BusRoute,
  vehicle: MapVehicle,
  lookahead = VEHICLE_LOOKAHEAD_STOPS,
): Stop | undefined {
  const stops = route.directions[vehicle.direction].stops
  if (stops.length === 0) return undefined

  const nearestIndex = nearestRouteStopIndex(stops, vehicle)

  return stops[Math.min(nearestIndex + Math.max(0, lookahead), stops.length - 1)]
}

/**
 * Return the route-order index of the stop nearest to a live vehicle.
 *
 * VehicleState gives us coordinates, not a stop ordinal.  The route's ordered
 * stop geometry is therefore the only stable way to tell whether a bus has
 * already passed a selected stop.  Keep ties deterministic (the earlier route
 * stop wins), matching the checkpoint planner above.
 */
export function nearestRouteStopIndex(stops: Stop[], vehicle: MapVehicle): number {
  if (stops.length === 0) return -1

  let nearestIndex = 0
  let nearestDistance = Number.POSITIVE_INFINITY
  for (let index = 0; index < stops.length; index += 1) {
    const stop = stops[index]!
    const latDistance = stop.lat - vehicle.lat
    const lngDistance = stop.lng - vehicle.lng
    const distance = latDistance * latDistance + lngDistance * lngDistance
    // Strict comparison makes coordinate ties deterministic: route order wins.
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestIndex = index
    }
  }
  return nearestIndex
}

/**
 * Select vehicles from a route snapshot that are still approaching a stop.
 *
 * Route polling discovers a complete fleet snapshot, while a stop view needs
 * only buses that will reach one stop in the reported direction.  A vehicle is
 * considered approaching when the nearest stop in its direction is at or
 * before the selected stop in that direction's route order.  The returned
 * array is a new array and preserves snapshot ordering/objects for last-known
 * handling and exact vehicle navigation.
 */
export function filterVehiclesAtStop(
  route: BusRoute,
  stopId: number,
  vehicles: MapVehicle[],
): MapVehicle[] {
  return vehicles.filter((vehicle) => {
    if (vehicle.routeCode !== route.code) return false
    const stops = route.directions[vehicle.direction].stops
    const targetIndex = stops.findIndex((stop) => stop.id === stopId)
    if (targetIndex < 0) return false
    return nearestRouteStopIndex(stops, vehicle) <= targetIndex
  })
}

/**
 * Project a route-wide fleet snapshot onto one selected stop.
 *
 * Route polling's `secondsToStop` and `distanceToStop` describe the checkpoint
 * used by that route poll, not necessarily the stop a user opened.  Use the
 * vehicle coordinates and the selected stop geometry for the display instead.
 * A moving vehicle uses its reported speed; when the feed reports zero/unknown
 * speed, the checkpoint's distance/ETA ratio provides a conservative fallback
 * instead of presenting a checkpoint ETA as if it were exact for this stop.
 */
export function vehiclesAtStop(
  route: BusRoute,
  stopId: number,
  vehicles: MapVehicle[],
): MapVehicle[] {
  return filterVehiclesAtStop(route, stopId, vehicles).map((vehicle) => {
    const targetStop = route.directions[vehicle.direction].stops.find((stop) => stop.id === stopId)
    if (!targetStop) return vehicle

    const distanceToStop = Math.round(
      haversineMeters(vehicle.lat, vehicle.lng, targetStop.lat, targetStop.lng),
    )
    const speedMetersPerSecond =
      vehicle.speed !== undefined && vehicle.speed > 0
        ? (vehicle.speed * 1_000) / 3_600
        : vehicle.distanceToStop !== undefined &&
            vehicle.distanceToStop > 0 &&
            vehicle.secondsToStop !== undefined &&
            vehicle.secondsToStop > 0
          ? vehicle.distanceToStop / vehicle.secondsToStop
          : undefined
    const secondsToStop =
      distanceToStop === 0
        ? 0
        : speedMetersPerSecond === undefined
          ? undefined
          : Math.ceil(distanceToStop / speedMetersPerSecond)

    const projected = { ...vehicle, distanceToStop }
    if (secondsToStop === undefined) {
      // Do not leak a checkpoint ETA into a selected-stop row when there is no
      // usable speed/telemetry from which to derive this stop's own estimate.
      delete projected.secondsToStop
      delete projected.minutesToNextStop
      delete projected.nextStop
    } else {
      projected.secondsToStop = secondsToStop
      projected.minutesToNextStop = Math.ceil(secondsToStop / 60)
      projected.nextStop = targetStop.name
    }
    return projected
  })
}

function targetKey(target: VehicleStateTarget): string {
  return `${target.stationId}\u0000${target.routeCode}\u0000${target.plate ?? ''}`
}

export function deduplicateVehicleStateTargets<T extends VehicleStateTarget>(targets: T[]): T[] {
  const unique = new Map<string, T>()
  for (const target of targets) unique.set(targetKey(target), target)
  return [...unique.values()]
}

export function plannedSteadyRouteCommandCount(route: BusRoute): number {
  return route.busCount + Math.ceil(collectRouteStationIds(route).length / DISCOVERY_SLICE_COUNT)
}

export function calculateLiveRequestBudget(dataset: TransitDataset) {
  const routesByStop = new Map<number, Set<string>>()
  let maximumRouteCommands = 0

  for (const route of dataset.routes) {
    maximumRouteCommands = Math.max(maximumRouteCommands, plannedSteadyRouteCommandCount(route))
    for (const stationId of collectRouteStationIds(route)) {
      const codes = routesByStop.get(stationId) ?? new Set<string>()
      codes.add(route.code)
      routesByStop.set(stationId, codes)
    }
  }

  let maximumStopCommands = 0
  for (const codes of routesByStop.values()) {
    // Stop polling sends one combined VehicleState command containing every
    // distinct route serving the stop, regardless of route count.
    maximumStopCommands = Math.max(maximumStopCommands, codes.size > 0 ? 1 : 0)
  }
  return { maximumRouteCommands, maximumStopCommands }
}
